
import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import { useFeatureFlags } from 'components/shared/feature-flags';

import {
  SkeletonLoader,
  NoGoalsDefined,
  NoToplineGoals,
} from 'containers/user/home/components/widgets/goals-widget/goals-widget-states';
import GoalsCarusel from 'containers/user/home/components/widgets/goals-widget/goal-carusel';
import ErrorWidget from 'containers/user/home/components/widgets/error-widget';
import NavButton from 'containers/user/home/components/shared/navigation-button';
import { MONITORING_GOALS_TAB_PATH } from 'containers/user/critical-elements-monitoring/monitoring-constants';

import Icons from 'assets/icons';

export const goalsConfig = {
  colSpan: 1,
  rowSpan: {
    default: 9,
  },
};

interface Goal {
  summary?: string;
  [key: string]: unknown;
}

const renderContent = (goals: Goal[], error: unknown): React.JSX.Element => {
  if (error) return <ErrorWidget />;
  if (!goals.length) return <NoToplineGoals />;

  return <GoalsCarusel goals={goals} />;
};

interface GoalsWidgetProps {
  loading?: boolean;
  error?: unknown;
  goals?: Goal[];
  hasClientGoals?: boolean;
}

const GoalsWidget = ({
  loading,
  error,
  goals = [],
  hasClientGoals = true,
}: GoalsWidgetProps): React.JSX.Element => {
  const { featureFlags } = useFeatureFlags();
  const hasMonitoringGoalsTab =
    featureFlags['critical-elements-monitoring'] && featureFlags['tab-goals'];

  const handleEmailClick = (): void => {
    window.location.href = 'mailto:clientsupport@marketviewedu.com';
  };

  if (loading) return <SkeletonLoader />;
  if (!hasClientGoals)
    return <NoGoalsDefined handleEmailClick={handleEmailClick} />;

  return (
    <Stack
      sx={{
        height: '100%',
        bgcolor: 'common.white',
        borderWidth: '1px',
        borderColor: 'tokens.navy.200',
        borderRadius: '0.5rem',
        p: 2.5,
        pt: 2,
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 2 }}>
        <Icons.Target className="h-5 w-5 text-navy-400" />
        <Box
          sx={{ fontWeight: 700, fontSize: '1.25rem', lineHeight: '1.75rem' }}
        >
          Your goals
        </Box>
      </Stack>

      {renderContent(goals, error)}

      {!error && hasMonitoringGoalsTab && (
        <NavButton
          link={MONITORING_GOALS_TAB_PATH}
          text="View your goals"
          customClass="mt-4"
        />
      )}
    </Stack>
  );
};

export default GoalsWidget;
